import React, { useRef, useState } from 'react';
import axios from '../../api/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Download, Upload, Database, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BackupTab = () => {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get('/backup/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.setAttribute('download', `backup_crm_${timestamp}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup descargado correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al exportar la base de datos');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast.error('Solo se permiten archivos .json');
      return;
    }

    if (!window.confirm('¿Estás seguro? Esto REEMPLAZARÁ todos los datos actuales con los del backup.')) {
      fileInputRef.current.value = '';
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post('/backup/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const result = response.data;
      const details = Object.entries(result.collections)
        .map(([col, count]) => `${col}: ${count}`)
        .join(', ');
      toast.success(`Importación completada: ${result.total} documentos (${details})`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar la base de datos');
    } finally {
      setImporting(false);
      fileInputRef.current.value = '';
    }
  };

  return (
    <Card data-testid="backup-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database size={20} />
          Backup de Base de Datos
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Export */}
          <div className="border rounded-lg p-6 space-y-4">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Download size={18} className="text-blue-600" />
              Exportar
            </h3>
            <p className="text-sm text-gray-600">
              Descarga una copia completa de la base de datos en formato JSON.
              Incluye todas las colecciones: muestras, clientes, destinos, ensayos, estados y usuarios.
            </p>
            <Button
              onClick={handleExport}
              disabled={exporting}
              className="w-full"
              data-testid="backup-export-btn"
            >
              {exporting ? (
                <><Loader2 size={16} className="animate-spin mr-2" /> Exportando...</>
              ) : (
                <><Download size={16} className="mr-2" /> Descargar Backup</>
              )}
            </Button>
          </div>

          {/* Import */}
          <div className="border rounded-lg p-6 space-y-4">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Upload size={18} className="text-green-600" />
              Importar
            </h3>
            <p className="text-sm text-gray-600">
              Restaura la base de datos desde un archivo de backup JSON.
              Esto reemplazará todos los datos actuales.
            </p>
            <input
              type="file"
              accept=".json"
              ref={fileInputRef}
              onChange={handleImport}
              className="hidden"
              data-testid="backup-import-input"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="w-full"
              data-testid="backup-import-btn"
            >
              {importing ? (
                <><Loader2 size={16} className="animate-spin mr-2" /> Importando...</>
              ) : (
                <><Upload size={16} className="mr-2" /> Subir Backup</>
              )}
            </Button>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800">
            <strong>Nota:</strong> La importación reemplaza completamente los datos existentes.
            Asegúrate de descargar un backup antes de importar.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default BackupTab;
